import type { FoodId } from "./gameplay-config";

export type FoodSilhouette = "kite" | "lobed" | "oval-striped" | "star";

export interface FoodVisualStyle {
  readonly accentColor: number;
  readonly fillColor: number;
  readonly idlePulseAmount: number;
  readonly outlineColor: number;
  readonly silhouette: FoodSilhouette;
}

export interface ArenaSandShadow {
  readonly color: number;
  readonly height: number;
  readonly width: number;
  readonly x: number;
  readonly y: number;
}

export interface ArenaDetail {
  readonly color: number;
  readonly length: number;
  readonly width: number;
  readonly x: number;
  readonly y: number;
}

export interface ArenaPatch {
  readonly color: number;
  readonly radius: number;
  readonly x: number;
  readonly y: number;
}

export const foodVisualConfig: {
  readonly appearanceDurationMs: number;
  readonly appearanceMinimumScale: number;
  readonly arena: {
    readonly backgroundColor: number;
    readonly boundaryColor: number;
    readonly boundaryOutlineColor: number;
    readonly detailAlpha: number;
    readonly details: readonly ArenaDetail[];
    readonly fieldColor: number;
    readonly patchAlpha: number;
    readonly patches: readonly ArenaPatch[];
    readonly sandShadowAlpha: number;
    readonly sandShadows: readonly ArenaSandShadow[];
  };
  readonly foodHaloColor: number;
  readonly foodHaloGap: number;
  readonly foodIdlePulseDurationMs: number;
  readonly foodMinimumRenderedDiameter: number;
  readonly pickupBurstColor: number;
  readonly pickupBurstDurationMs: number;
  readonly pickupBurstMaximumRadius: number;
  readonly pickupBurstMinimumRadius: number;
  readonly reducedMotionCueDurationMs: number;
  readonly styles: Readonly<Record<FoodId, FoodVisualStyle>>;
} = {
  appearanceDurationMs: 280,
  appearanceMinimumScale: 0.84,
  arena: {
    backgroundColor: 0x153c42,
    boundaryColor: 0xf3c969,
    boundaryOutlineColor: 0xffedab,
    detailAlpha: 0.28,
    details: [
      { color: 0x77aa72, length: 24, width: 5, x: 270, y: 620 },
      { color: 0x679f78, length: 30, width: 5, x: 760, y: 260 },
      { color: 0x78a96d, length: 26, width: 5, x: 1_320, y: 1_330 },
      { color: 0x5d9c81, length: 32, width: 5, x: 1_850, y: 540 },
      { color: 0x72a66d, length: 28, width: 5, x: 2_150, y: 1_280 },
      { color: 0x679f78, length: 26, width: 5, x: 2_580, y: 310 },
      { color: 0x77aa72, length: 32, width: 5, x: 3_180, y: 660 },
      { color: 0x5d9c81, length: 24, width: 5, x: 420, y: 1_920 },
      { color: 0x72a66d, length: 30, width: 5, x: 1_150, y: 2_080 },
      { color: 0x78a96d, length: 28, width: 5, x: 2_040, y: 1_920 },
      { color: 0x679f78, length: 32, width: 5, x: 2_770, y: 2_120 },
      { color: 0x77aa72, length: 26, width: 5, x: 3_310, y: 1_640 },
    ],
    fieldColor: 0x2b674d,
    patchAlpha: 0.16,
    patches: [
      { color: 0x4b8b61, radius: 150, x: 430, y: 330 },
      { color: 0x397c66, radius: 190, x: 990, y: 1_170 },
      { color: 0x4d8d60, radius: 165, x: 1_540, y: 410 },
      { color: 0x3c806f, radius: 220, x: 2_020, y: 1_150 },
      { color: 0x4b8b61, radius: 180, x: 2_780, y: 360 },
      { color: 0x397c66, radius: 205, x: 3_180, y: 1_140 },
      { color: 0x4d8d60, radius: 175, x: 570, y: 2_030 },
      { color: 0x3c806f, radius: 195, x: 1_760, y: 2_050 },
      { color: 0x4b8b61, radius: 170, x: 2_890, y: 1_930 },
    ],
    sandShadowAlpha: 0.12,
    sandShadows: [
      { color: 0x194f46, height: 34, width: 220, x: 410, y: 520 },
      { color: 0x205746, height: 28, width: 180, x: 950, y: 360 },
      { color: 0x194f46, height: 42, width: 260, x: 1_430, y: 960 },
      { color: 0x205746, height: 32, width: 210, x: 2_050, y: 480 },
      { color: 0x194f46, height: 36, width: 240, x: 2_750, y: 850 },
      { color: 0x205746, height: 30, width: 190, x: 3_260, y: 520 },
      { color: 0x194f46, height: 38, width: 230, x: 620, y: 1_760 },
      { color: 0x205746, height: 44, width: 280, x: 1_500, y: 2_050 },
      { color: 0x194f46, height: 30, width: 200, x: 2_290, y: 1_740 },
      { color: 0x205746, height: 40, width: 250, x: 3_110, y: 1_960 },
    ],
  },
  foodHaloColor: 0xfff7d6,
  foodHaloGap: 4,
  foodIdlePulseDurationMs: 1_600,
  foodMinimumRenderedDiameter: 20,
  pickupBurstColor: 0xffed7a,
  pickupBurstDurationMs: 420,
  pickupBurstMaximumRadius: 46,
  pickupBurstMinimumRadius: 24,
  reducedMotionCueDurationMs: 900,
  styles: {
    berry: {
      accentColor: 0xfff7fb,
      fillColor: 0xf04b98,
      idlePulseAmount: 0.04,
      outlineColor: 0x4a1534,
      silhouette: "lobed",
    },
    citrus: {
      accentColor: 0xfff2a8,
      fillColor: 0xffa928,
      idlePulseAmount: 0.05,
      outlineColor: 0x5b3500,
      silhouette: "kite",
    },
    melon: {
      accentColor: 0xb8f06a,
      fillColor: 0xff6f91,
      idlePulseAmount: 0.1,
      outlineColor: 0x4e1e31,
      silhouette: "oval-striped",
    },
    starfruit: {
      accentColor: 0xfff5bd,
      fillColor: 0xffd84d,
      idlePulseAmount: 0.14,
      outlineColor: 0x574400,
      silhouette: "star",
    },
  },
};
